import {
  useDataProvider,
  ReadOnly,
  SnackbarApiErrorMessage,
  AutoComplete as UIFAutoComplete,
  useEntityPermission,
  useSnackbar,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { forwardRef, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

// Using List API V1 without operator support
export const AutoCompleteWrapper = forwardRef(
  ({ field, customConfig, ...props }, ref) => {
    const { creatable, nameKey, valueKey, apiPath, searchKey, isMulti } =
      customConfig;
    const { getValues } = useFormContext();
    const { isHidden, isReadOnly } = useEntityPermission();

    const currentvalue = getValues(field?.name)?.label;

    if (isReadOnly(props?.permission, currentvalue)) {
      return <ReadOnly name={field?.name}>{currentvalue}</ReadOnly>;
    }

    if (isHidden(props?.permission)) {
      return <input type="hidden" name={field?.name} value={currentvalue} />;
    }

    return (
      <AutoComplete
        ref={ref}
        nameKey={nameKey}
        valueKey={valueKey}
        apiPath={apiPath}
        searchKey={searchKey}
        field={field}
        creatable={creatable}
        isMulti={isMulti}
        {...props}
      />
    );
  },
);

// Using List API V2 with operator support and Static List Values(masterdata)
export const AutoCompleteWrapperV2 = forwardRef(
  ({ field, customConfig, ...props }, ref) => {
    const { creatable, nameKey, valueKey, apiPath, searchKey, isMulti } =
      customConfig;
    const { getValues } = useFormContext();
    const { isHidden, isReadOnly } = useEntityPermission();

    const currentvalue = getValues(field?.name)?.label;

    if (isReadOnly(props?.permission, currentvalue)) {
      return <ReadOnly name={field?.name}>{currentvalue}</ReadOnly>;
    }

    if (isHidden(props?.permission)) {
      return <input type="hidden" name={field?.name} value={currentvalue} />;
    }

    return (
      <AutoComplete
        ref={ref}
        nameKey={nameKey}
        valueKey={valueKey}
        apiPath={apiPath}
        searchKey={searchKey}
        field={field}
        creatable={creatable}
        isMulti={isMulti}
        apiVersion="2"
        {...props}
      />
    );
  },
);

const AutoComplete = forwardRef(
  (
    {
      overwriteProps = {},
      apiPath,
      searchKey,
      nameKey,
      valueKey,
      onChange,
      field,
      creatable,
      isMulti,
      apiVersion = '1',
      ...props
    },
    ref,
  ) => {
    const [defaultOptions, setDefaultOptions] = useState([]);
    const snackbar = useSnackbar();
    const [autoDisplayIfOneValue, setAutoDisplayIfOneValue] = useState(false);

    const autoDisplayIfOneValueProp = field?.autoDisplayIfOneValue !== false;

    const provider =
      process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
    const { get, getApiUrl } = useDataProvider(provider);

    const { getValues, control, setValue } = useFormContext();
    const currentValue = getValues(field?.name);

    const getEntities = async (searchValue = '') => {
      let result = null;

      // append searchkey and value only if searchKey is defined andd value is not empty
      let searchQuery = searchValue ? `&${searchKey}=${searchValue || ''}` : '';
      if (apiVersion === '2') {
        searchQuery = searchValue
          ? `&${searchKey}=$like:${searchValue || ''}`
          : '';
      }

      const apiPathHasParams = apiPath.indexOf('?') !== -1;

      result = await get(
        `${getApiUrl()}/sxcl/v1/${apiPath}${
          apiPathHasParams ? '&' : '?'
        }limit=50${searchQuery}`,
      );

      if (result.ok) {
        const resultsData =
          result?.data?.data?.map(item => ({
            label: item[nameKey],
            value: item[valueKey],
          })) || [];
        return { data: resultsData, total: result?.data?.total || 0 };
      }
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={result?.errors} />,
      });
      return null;
    };

    const initialLoad = async currentValueArg => {
      const result = await getEntities('');

      if (currentValueArg?.label) {
        const { data = null } = result;
        setDefaultOptions([
          currentValueArg,
          ...(data
            ? data.filter(item => item.value !== currentValueArg.value)
            : []),
        ]);
      } else {
        setDefaultOptions(result?.data);
      }

      if (autoDisplayIfOneValueProp && result?.total === 1) {
        setAutoDisplayIfOneValue(true);
      } else {
        setAutoDisplayIfOneValue(false);
      }
    };

    const loadOptions = async (searchValue, callback) => {
      if (!searchValue) return [];
      if (searchValue.length < 2) return [];
      const resultsData = await getEntities(searchValue);
      return callback(resultsData?.data);
    };

    useEffect(() => {
      initialLoad(currentValue);
    }, [currentValue]);

    useEffect(() => {
      if (autoDisplayIfOneValue && defaultOptions[0]?.value) {
        setValue(field?.name, defaultOptions[0], { shouldDirty: true });
      }
    }, [autoDisplayIfOneValue]);

    if (autoDisplayIfOneValue) {
      return <ReadOnly name={field?.name}>{defaultOptions[0].label}</ReadOnly>;
    }

    return (
      <UIFAutoComplete
        control={control}
        ref={ref}
        defaultOptions={defaultOptions}
        loadOptions={loadOptions}
        {...props}
        id={field?.name.split('.')[0]}
        isClearable
        overwriteProps={overwriteProps}
        creatable={creatable}
        isMulti={isMulti}
      />
    );
  },
);

export default observer(AutoComplete);
